"use client";
import { useState, useEffect, useCallback } from "react";
import { Swiper, SwiperSlide } from "swiper/react";
import { Swiper as SwiperClass } from "swiper/types";
import cn from "classnames";
import s from "./SamplesSlider.module.scss";
import { SampleCard, SampleCardProps } from "components/shared/SampleCard";
import { Button } from "components/UI/buttons/Button";
import { api } from "utils/httpClient";
import { sampleToSampleCard } from "utils/helpers";
import { SampleType } from "types/shared";
import { CTACard, CTACardProps } from "@/components/shared/CTACard";
import React from "react";

type SamplesSliderProps = {
  className?: string;
  items: SampleCardProps[];
  filterFn?: (item: SampleType) => boolean;
  btnText?: string;
  pages?: number;
  queryParams?: Record<string, any>;
  ctaIndex?: number;
  ctaCard?: Omit<CTACardProps, "className">;
};

export const SamplesSlider = ({
  items,
  className,
  btnText,
  pages,
  queryParams,
  filterFn,
  ctaIndex,
  ctaCard,
}: SamplesSliderProps) => {
  const [swiper, setSwiper] = useState<SwiperClass | null>(null);
  const [isDragging, setDragging] = useState(false);
  const [page, setPage] = useState(1);
  const [loading, setLoading] = useState(false);
  // @ts-ignore
  const [extraSamples, setExtraSamples] = useState<SampleCardProps[]>(
    items.length > queryParams?.limit ? items.slice(queryParams?.limit - items.length) : []
  );

  if (items.length > queryParams?.limit && page === 1) {
    items.pop();
  }

  const loadMore = useCallback(async () => {
    if (page > Number(pages)) return;

    setLoading(true);

    try {
      const samplesRequest = await api.get(
        "/",
        {
          params: {
            ...queryParams,
            page,
            limit: filterFn ? queryParams?.limit + 1 : queryParams?.limit,
          },
        },
        { cache: "no-store" }
      );

      let results = samplesRequest.data.items.map(sampleToSampleCard);

      if (extraSamples.length) {
        results = [...extraSamples, ...results];
        setExtraSamples([]);
      }

      if (filterFn) {
        results = results.filter(filterFn);

        if (results.length > queryParams?.limit) {
          setExtraSamples([...results.slice(queryParams?.limit - results.length)]);
          results = results.slice(0, queryParams?.limit);
        }
      }

      results.forEach((item: any) => {
        items.push(item);
      });

      setLoading(false);
    } catch (err) {}
  }, [page, pages, queryParams, filterFn, extraSamples, items]);

  useEffect(() => {
    if (page === 1) return;
    loadMore().then();
  }, [page, loadMore]);

  return (
    <>
      <Swiper
        className={cn(s.samplesSlider, className, {
          [s.samplesSliderClicked]: isDragging,
        })}
        spaceBetween={16}
        slidesPerView={1}
        onSwiper={setSwiper}
        onTouchStart={() => setDragging(true)}
        onTouchEnd={() => setDragging(false)}
        speed={500}
        breakpoints={{
          576: {
            slidesPerView: 2,
            spaceBetween: 24,
          },
        }}
        onReachEnd={() => {
          if (loading || !btnText) return;
          setPage(page + 1);
        }}
      >
        {items.map((item, index) => (
          // * NOTE:
          // * React.Fragment instead of <></> -> allows me pass "key" prop in to level
          // *  & resolve "<Swiper> Warning: Each child in a list should have a unique "key" prop." issue
          <React.Fragment key={item.id}>
            {ctaCard && index === ctaIndex && (
              <SwiperSlide key={`cta-${item.id}`}>
                <CTACard {...ctaCard} className={s.ctaCard} />
              </SwiperSlide>
            )}
            <SwiperSlide key={`item-${item.id}`}>
              <SampleCard {...item} />
            </SwiperSlide>
          </React.Fragment>
        ))}
      </Swiper>

      {btnText && page < Number(pages) && (
        <div className={s.btnWrap}>
          <Button
            tag="button"
            type="secondary"
            loading={loading}
            className={s.moreBtn}
            onClick={() => {
              setPage(page + 1);
            }}
          >
            {btnText}
          </Button>
        </div>
      )}
    </>
  );
};
